// A signed-in client's own appointments: tap one to cancel or move it, the same
// two actions a staff member has on the calendar. The difference from the
// operator's screen is the SCOPE, not the shape — every call goes to v1-widget,
// which resolves the customer from their own JWT and answers `booking_not_found`
// for anybody else's id. Nothing here sends a customer id, so there is no
// parameter a client could change to reach another person's diary.
//
// Two ways in, one dialog: the account menu, and the "upcoming appointments"
// card the booking chat shows a signed-in client (operator, 2026-10-12 — behind
// the menu alone it was too hard to find). The card asks the host to open this
// through the `hj-manage-appointments` event, so "manage" is defined once.
import { useCallback, useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import { Loader2 } from 'lucide-react'
import {
  listMyBookings,
  cancelMyBooking,
  rescheduleMyBooking,
  listSlotsForService,
  type ClientBooking,
  type ClientBookingsResponse,
  type ClientSlot,
} from '@/lib/api'
import { fmtDayDate, fmtDuration, fmtTime, fmtMoney } from '@/lib/format'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

/** How far ahead the reschedule picker looks, in days. */
const RESCHEDULE_WINDOW_DAYS = 28

function addDays(dateStr: string, days: number): string {
  const [y, m, d] = dateStr.split('-').map(Number)
  const dt = new Date(Date.UTC(y, m - 1, d + days))
  return dt.toISOString().slice(0, 10)
}

function durationLabel(minutes: number | null | undefined): string | null {
  if (minutes === null || minutes === undefined || !Number.isFinite(minutes) || minutes <= 0) return null
  return minutes < 60 ? `${minutes} min` : fmtDuration(minutes)
}

/** '17:20–17:50' — the end is shown because a client plans the rest of their day around it. */
function timeRange(b: ClientBooking): string {
  return b.end ? `${fmtTime(b.start)}–${fmtTime(b.end)}` : fmtTime(b.start)
}

/** What the session costs or how it was paid, in the client's terms. null when nothing is worth saying. */
function paymentLabel(b: ClientBooking, currency: string): string | null {
  switch (b.payment) {
    case 'paid': return 'Paid'
    case 'package': return 'Paid with package credit'
    case 'free': return 'Free'
    case 'part_paid': return `${fmtMoney(b.balance_due ?? 0, currency)} still to pay`
    case 'unpaid': return 'Not paid yet'
    default: return null
  }
}

/** The short tag on a list row: settled sessions say so, anything else shows its price. */
function rowTag(b: ClientBooking, currency: string): string | null {
  if (b.payment === 'paid' || b.payment === 'package') return 'Paid'
  if (b.payment === 'free') return 'Free'
  return b.price != null ? fmtMoney(b.price, currency) : null
}

export interface ClientAppointmentsDialogProps {
  slug: string
  brandColor: string
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Open straight onto this appointment — the chat card's rows name one. */
  focusBookingId?: string | null
  /** A cancel or a move went through, so anything else showing the diary should re-read. */
  onChanged?: () => void
}

export function ClientAppointmentsDialog({
  slug,
  brandColor,
  open,
  onOpenChange,
  focusBookingId = null,
  onChanged,
}: ClientAppointmentsDialogProps) {
  const [data, setData] = useState<ClientBookingsResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [failed, setFailed] = useState(false)
  const [selected, setSelected] = useState<ClientBooking | null>(null)

  const refresh = useCallback(async (): Promise<ClientBookingsResponse | null> => {
    setLoading(true)
    try {
      const res = await listMyBookings(slug)
      setData(res)
      setFailed(false)
      return res
    } catch {
      setFailed(true)
      return null
    } finally {
      setLoading(false)
    }
  }, [slug])

  // Read on OPEN, not on mount: the booking screen behind this must not spend a
  // request on appointments nobody has asked to see.
  useEffect(() => {
    if (!open) return
    setSelected(null)
    let cancelled = false
    void refresh().then((res) => {
      // A focus id that is no longer upcoming (cancelled elsewhere, or past) just
      // shows the list — never a stale appointment.
      if (cancelled || !res || !focusBookingId) return
      setSelected(res.bookings.find((b) => b.id === focusBookingId) ?? null)
    })
    return () => { cancelled = true }
  }, [open, refresh, focusBookingId])

  const bookings = data?.bookings ?? []
  const currency = data?.currency || 'AUD'

  // Grouped by day: a visit of several treatments reads as one morning, not as
  // a run of near-identical rows.
  const byDay = new Map<string, ClientBooking[]>()
  for (const b of bookings) {
    const day = b.start.slice(0, 10)
    byDay.set(day, [...(byDay.get(day) ?? []), b])
  }
  const total = data?.total ?? null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-[440px]">
        {selected ? (
          <ManageView
            slug={slug}
            booking={selected}
            data={data}
            brandColor={brandColor}
            onBack={() => setSelected(null)}
            onDone={() => {
              setSelected(null)
              void refresh()
              onChanged?.()
            }}
          />
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Your appointments</DialogTitle>
              <DialogDescription>Tap one to see the details, change the time or cancel it.</DialogDescription>
            </DialogHeader>

            {loading ? (
              <p className="flex items-center gap-2 py-4 text-[13px] text-muted-foreground">
                <Loader2 className="size-4 animate-spin" aria-hidden /> Loading…
              </p>
            ) : failed ? (
              <p className="py-4 text-[13px] text-muted-foreground">
                Couldn&apos;t load your appointments just now. Please try again.
              </p>
            ) : bookings.length === 0 ? (
              <p className="py-4 text-[13px] text-muted-foreground">
                You have no upcoming appointments. Book one below.
              </p>
            ) : (
              <div className="flex flex-col gap-4">
                {[...byDay.entries()].map(([day, dayBookings]) => (
                  <section key={day} className="flex flex-col gap-2">
                    <h3 className="text-[12px] font-semibold uppercase tracking-wide text-muted-foreground">
                      {fmtDayDate(`${day}T00:00`)}
                    </h3>
                    <ul className="flex flex-col gap-2">
                      {dayBookings.map((b) => {
                        const duration = durationLabel(b.duration_minutes)
                        const tag = rowTag(b, currency)
                        return (
                          <li key={b.id}>
                            <button
                              type="button"
                              onClick={() => setSelected(b)}
                              className="flex w-full cursor-pointer items-center justify-between gap-3 rounded-xl border border-border bg-background px-3.5 py-3 text-left hover:bg-muted/50"
                            >
                              <span className="min-w-0">
                                <span className="block truncate text-sm font-semibold text-foreground">
                                  {b.service.name ?? 'Appointment'}
                                </span>
                                <span className="block text-[12.5px] text-muted-foreground">
                                  {timeRange(b)}{duration ? ` · ${duration}` : ''}
                                </span>
                                {b.practitioner && (
                                  <span className="block truncate text-[12.5px] text-muted-foreground">
                                    with {b.practitioner}
                                  </span>
                                )}
                              </span>
                              <span className="flex shrink-0 flex-col items-end gap-0.5">
                                {tag && <span className="text-[12px] text-muted-foreground">{tag}</span>}
                                <span className="text-[12px] font-medium" style={{ color: brandColor }}>
                                  Manage
                                </span>
                              </span>
                            </button>
                          </li>
                        )
                      })}
                    </ul>
                  </section>
                ))}
                {total !== null && total > bookings.length && (
                  <p className="text-[12px] text-muted-foreground">
                    Showing your next {bookings.length} of {total} appointments.
                  </p>
                )}
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}

type Mode = 'view' | 'reschedule' | 'confirm-cancel'

function DetailRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex justify-between gap-4 py-1.5 text-[13.5px]">
      <dt className="shrink-0 text-muted-foreground">{label}</dt>
      <dd className="min-w-0 text-right text-foreground">{children}</dd>
    </div>
  )
}

function ManageView({
  slug,
  booking,
  data,
  brandColor,
  onBack,
  onDone,
}: {
  slug: string
  booking: ClientBooking
  data: ClientBookingsResponse | null
  brandColor: string
  onBack: () => void
  onDone: () => void
}) {
  const [mode, setMode] = useState<Mode>('view')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [slots, setSlots] = useState<ClientSlot[] | null>(null)

  const currency = data?.currency || 'AUD'
  const business = data?.business ?? null
  const policy = data?.cancellation_policy?.trim() || null

  const loadSlots = useCallback(async () => {
    if (!booking.service.id) {
      setError('This appointment cannot be moved here — please contact the business.')
      return
    }
    setMode('reschedule')
    setSlots(null)
    setError(null)
    try {
      const today = new Date().toISOString().slice(0, 10)
      const found = await listSlotsForService(
        slug,
        booking.service.id,
        `${today}T00:00`,
        `${addDays(today, RESCHEDULE_WINDOW_DAYS)}T23:59`,
      )
      setSlots(found)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load available times.')
      setSlots([])
    }
  }, [booking.service.id, slug])

  const act = async (run: () => Promise<unknown>) => {
    setBusy(true)
    setError(null)
    try {
      await run()
      onDone()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Something went wrong. Please try again.')
    } finally {
      setBusy(false)
    }
  }

  // Grouped by day so the picker reads like a diary rather than a list of 200 times.
  const byDay = new Map<string, ClientSlot[]>()
  for (const slot of slots ?? []) {
    const day = slot.start.slice(0, 10)
    const list = byDay.get(day) ?? []
    list.push(slot)
    byDay.set(day, list)
  }

  const duration = durationLabel(booking.duration_minutes)
  const payment = paymentLabel(booking, currency)

  return (
    <>
      <DialogHeader>
        <DialogTitle>{booking.service.name ?? 'Appointment'}</DialogTitle>
        <DialogDescription>
          {fmtDayDate(booking.start)} · {timeRange(booking)}
        </DialogDescription>
      </DialogHeader>

      {mode === 'view' && (
        <dl className="divide-y divide-border rounded-xl border border-border px-3.5 py-1">
          <DetailRow label="Date">{fmtDayDate(booking.start)}</DetailRow>
          <DetailRow label="Time">{timeRange(booking)}</DetailRow>
          {duration && <DetailRow label="Duration">{duration}</DetailRow>}
          {booking.practitioner && <DetailRow label="With">{booking.practitioner}</DetailRow>}
          {booking.price != null && booking.payment !== 'free' && (
            <DetailRow label="Price">{fmtMoney(booking.price, currency)}</DetailRow>
          )}
          {payment && <DetailRow label="Payment">{payment}</DetailRow>}
          {business?.address && (
            <DetailRow label="Where">
              {business.name}
              <span className="block text-muted-foreground">{business.address}</span>
            </DetailRow>
          )}
          {business?.phone && (
            <DetailRow label="Phone">
              <a href={`tel:${business.phone.replace(/\s+/g, '')}`} className="underline" style={{ color: brandColor }}>
                {business.phone}
              </a>
            </DetailRow>
          )}
        </dl>
      )}

      {error && <p className="text-[13px] text-destructive">{error}</p>}

      {mode === 'view' && (
        <div className="flex flex-col gap-2">
          <Button
            type="button"
            className="w-full text-white"
            style={{ backgroundColor: brandColor }}
            onClick={() => void loadSlots()}
            disabled={busy}
          >
            Change time
          </Button>
          <Button
            type="button"
            variant="outline"
            className="w-full text-destructive"
            onClick={() => setMode('confirm-cancel')}
            disabled={busy}
          >
            Cancel appointment
          </Button>
          <Button type="button" variant="ghost" className="w-full" onClick={onBack} disabled={busy}>
            Back
          </Button>
        </div>
      )}

      {mode === 'confirm-cancel' && (
        <div className="flex flex-col gap-2">
          <p className="text-[13.5px] text-muted-foreground">
            Cancel this appointment? The time goes back into the diary straight away.
          </p>
          {/* Shown before the client commits — this is the moment the policy matters. */}
          {policy && (
            <p className="rounded-lg bg-muted px-3 py-2 text-[12.5px] whitespace-pre-line text-muted-foreground">
              <span className="font-semibold text-foreground">Cancellation policy: </span>
              {policy}
            </p>
          )}
          <Button
            type="button"
            variant="destructive"
            className="w-full"
            disabled={busy}
            onClick={() => void act(() => cancelMyBooking(slug, booking.id))}
          >
            {busy ? <Loader2 className="size-4 animate-spin" aria-hidden /> : 'Yes, cancel it'}
          </Button>
          <Button type="button" variant="outline" className="w-full" onClick={() => setMode('view')} disabled={busy}>
            Keep it
          </Button>
        </div>
      )}

      {mode === 'reschedule' && (
        <div className="flex flex-col gap-3">
          {slots === null ? (
            <p className="flex items-center gap-2 text-[13px] text-muted-foreground">
              <Loader2 className="size-4 animate-spin" aria-hidden /> Finding available times…
            </p>
          ) : byDay.size === 0 ? (
            <p className="text-[13px] text-muted-foreground">
              No open times in the next {RESCHEDULE_WINDOW_DAYS} days. Please contact the business.
            </p>
          ) : (
            [...byDay.entries()].map(([day, daySlots]) => (
              <div key={day}>
                <p className="mb-1 text-[12px] font-semibold text-muted-foreground">{fmtDayDate(`${day}T00:00`)}</p>
                <div className="flex flex-wrap gap-1.5">
                  {daySlots.map((slot) => (
                    <button
                      key={slot.start}
                      type="button"
                      disabled={busy}
                      onClick={() => void act(() => rescheduleMyBooking(slug, booking.id, slot.start))}
                      className={cn(
                        'cursor-pointer rounded-lg border border-border bg-background px-2.5 py-1.5 text-[13px] font-medium',
                        'hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50',
                      )}
                    >
                      {fmtTime(slot.start)}
                    </button>
                  ))}
                </div>
              </div>
            ))
          )}
          <Button type="button" variant="outline" className="w-full" onClick={() => setMode('view')} disabled={busy}>
            Back
          </Button>
        </div>
      )}
    </>
  )
}
